import classNames from 'classnames'
import { Xmark as Cancel } from 'iconoir-react'
import { useCallback, useEffect, useState } from 'react'
import ComboAsync from 'app/components/ui/combo/combo-async'
import type { WorkspaceJson } from 'app/types/entities'
import { SearchResponse } from 'app/services/search'
import { gqlFetchList } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'
import { OptionType } from 'types/OptionType'
import { useWorkspaceStore } from 'app/stores/StoreContext'

type Props = {
  onChange: (workspace: WorkspaceJson) => void | Promise<void>
  name?: string
  className?: string
  disabled?: boolean
  withBlankOption?: boolean
  workspace?: WorkspaceJson
  workspaceId?: string
  selectedWorkspaces?: WorkspaceJson[]
  inputClassName?: string
  onRemoveSelectedWorkspace?: (workspace: WorkspaceJson) => void
  showSearchInDropdown?: boolean
  queryURL?: string
  fetcher?: (params: { query: string }) => Promise<any>
}

export default function WorkspaceSelector({
  name,
  workspace,
  workspaceId,
  className,
  selectedWorkspaces,
  disabled = false,
  withBlankOption = true,
  inputClassName,
  onChange,
  onRemoveSelectedWorkspace,
  showSearchInDropdown,
  queryURL,
  fetcher: fetcherProp,
}: Props) {
  const blankOption = { name: ``, id: ``, value: `` }
  const [selected, setSelected] = useState<OptionType>(blankOption)
  let additionalChoices = { ...(withBlankOption && { additionalChoices: [blankOption] }) }
  const workspaceStore = useWorkspaceStore()

  const defaultFetcher = useCallback(
    (params: { query: string }) => gqlFetchList<WorkspaceJson>(`workspaces`, { search: params.query || undefined }),
    [],
  )
  const fetcher = fetcherProp ?? defaultFetcher

  const optionsMapper = (response: SearchResponse<WorkspaceJson>): OptionType[] => {
    debug([`workspace`, `\`toggles`], `mapping`, response.hits?.length)
    let _options = response.hits?.map((workspace) => ({
      id: workspace.id!,
      value: workspace.id!,
      name: workspace.name!,
    }))
    return _options
  }

  useEffect(() => {
    debug(`toggles`, `workspace selector`, { workspaceId })
    bg(async () => {
      if (workspaceId) {
        debug(`toggles`, `getting workspaces for id`, workspaceId)
        const result = await workspaceStore.fetchOne(workspaceId)
        const data = result.data as any
        const hits: WorkspaceJson[] = data?.workspaces?.hits ?? data?.hits ?? (data ? [data] : [])
        debug(`toggles`, `got workspaces for id`, workspaceId, hits)
        if (hits?.length) {
          let ws = hits[0]
          setSelected({
            id: ws.id!,
            name: ws.name!,
            value: ws.id!,
          })
        }
      }
    })
  }, [workspaceId])

  useEffect(() => {
    if (workspace?.id) {
      debug(`workspace`, `loaded workspace`, workspace.name)
      setSelected({
        id: workspace.id!,
        name: workspace.name!,
        value: workspace.id!,
      })
    } else {
      setSelected(blankOption)
    }
  }, [workspace])

  return (
    <div className={classNames(`workspace-selector`, className)}>
      <ComboAsync
        className="async-combo-selector"
        inputClassName={inputClassName}
        name={name}
        queryURL={queryURL}
        fetcher={fetcher}
        optionsMapper={optionsMapper}
        onChange={async (workspace) => {
          setSelected(workspace)
          await onChange(workspace)
        }}
        disabled={disabled}
        placeholder="Workspace Search"
        value={selected}
        showSearchInDropdown={showSearchInDropdown}
        {...additionalChoices}
      />
      {selectedWorkspaces && selectedWorkspaces.length > 0 && (
        <div className="mt-2 flex gap-2 rounded-lg border border-default p-4">
          <div className="inline-flex flex-wrap gap-2">
            {selectedWorkspaces?.map((option) => (
              <span
                key={option.id}
                className="flex items-baseline justify-center gap-3 rounded border border-default bg-surface-subtle p-2 text-body-sm text-strong"
              >
                {option.name}
                {!disabled && (
                  <Cancel
                    className="h-3 w-3 hover:cursor-pointer"
                    onClick={() => onRemoveSelectedWorkspace?.(option)}
                  />
                )}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
